import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { EmployeeAndTag, EmployeeTagType, CompanyRankingSettingsDto, Country } from '../../types'
import { PerInfoBox } from '../../components/custom_components/InfoBox'
import { InfoBoxTile } from '../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'
import { PerInfoBoxNumberElement } from '../../components/custom_components/InfoBox/InfoBoxNumberElement'
import { PerInfoBoxDivider } from '../../components/custom_components/InfoBox/InfoBoxDivider'
import { rankingUiProps } from './ranking.uiprops'
import { PerInfoBoxTagElement } from '../../components/custom_components/InfoBox/InfoBoxTagElement'
import { CompanySettingsRankingsSettingsForm } from '../companySettings/ranking/settings/CompanySettingsRankingsSettingsForm'
import PerText from '../../components/custom_components/Text'
import PerFlexRow from '../../components/custom_components/FlexRow'
import PerIcon from '../../components/custom_components/Icon'
import PerSpacer from '../../components/custom_components/Spacer'
import PerButtonCircle from '../../components/custom_components/ButtonCircle'

export type CompanySettingsRankingsInfoBoxProps = {
  infoBoxData: EmployeeAndTag[]
  isHR?: boolean
  companyId?: string
  country?: string
  settings: CompanyRankingSettingsDto
  canEdit?: boolean | null
}

export const RankingInfoBox: FC<CompanySettingsRankingsInfoBoxProps> = ({
  infoBoxData,
  companyId,
  country,
  isHR = false,
  settings,
  canEdit: isHrAdmin,
}) => {
  const { t } = useTranslation()
  const { tagColor, isPerformanceTag, isImpactOfLossTag, isRiskOfLossTag } = rankingUiProps()
  const totalOfEmployees = infoBoxData.length
  const availableEmployeeTags = Object.keys(EmployeeTagType).map((key) => {
    return {
      tagName: EmployeeTagType[key],
      value: infoBoxData.filter((e) => e.tags.filter((t) => t.type === EmployeeTagType[key]).length > 0).length,
    }
  })

  const performanceUntaggedCount = infoBoxData.filter(
    (e) => e.tags.filter((t) => isPerformanceTag(t.type as EmployeeTagType)).length === 0,
  ).length
  const impactOfLossUntaggedCount = infoBoxData.filter(
    (e) => e.tags.filter((t) => isImpactOfLossTag(t.type as EmployeeTagType)).length === 0,
  ).length
  const riskOfLossUntaggedCount = infoBoxData.filter(
    (e) => e.tags.filter((t) => isRiskOfLossTag(t.type as EmployeeTagType)).length === 0,
  ).length

  const lowCounts =
    availableEmployeeTags.filter((t) => t.tagName === EmployeeTagType.Low)[0].value +
    availableEmployeeTags.filter((t) => t.tagName === EmployeeTagType.EntryLevelLow)[0].value
  const minLowTagsNecessary = !!settings.lowPercentage ? Math.max(1, Math.round((settings.lowPercentage * totalOfEmployees) / 100)) : 0

  const topCounts = availableEmployeeTags.filter((t) => t.tagName === EmployeeTagType.Top)[0].value
  const maxTopTagsNecessary = !!settings.topPercentage ? Math.max(1, Math.round((settings.topPercentage * totalOfEmployees) / 100 || 0)) : 0

  const renderSettings = () => (
    <InfoBoxTile heading={t('rankingView.infoBox.settings.heading')} headingAlign="left" contentFlex="fixed">
      {companyId && country && (
        <CompanySettingsRankingsSettingsForm companyId={companyId} country={country as Country} settings={settings} disabled={!isHrAdmin} />
      )}
    </InfoBoxTile>
  )

  return (
    <div style={{ overflowX: 'auto' }}>
      <PerInfoBox scrollX>
        <InfoBoxTile heading={t('rankingView.infoBox.employeesHeading')} headingAlign="left" contentFlex="fixed">
          <PerInfoBoxNumberElement number={totalOfEmployees} />
        </InfoBoxTile>

        <PerInfoBoxDivider />

        {isHR && (
          <>
            {renderSettings()}
            <PerInfoBoxDivider />
          </>
        )}

        <InfoBoxTile
          heading={''}
          extendedHeading={
            <PerFlexRow width="100%" justify="space-between">
              <PerText appearance="tile-heading" align="left" mb="xxs" mt="none">
                {t('rankingView.infoBox.performance.heading')}
              </PerText>
              <PerFlexRow align="middle">
                <PerButtonCircle
                  icon="info_fill"
                  size="small"
                  color="secondary"
                  variant="pastel"
                  className="disable-shadow"
                  tooltip={
                    <PerText>
                      {t(
                        !!settings.tagAssignmentIsInProgress
                          ? 'rankingView.infoBox.performance.tooltipOpen'
                          : 'rankingView.infoBox.performance.tooltipClosed',
                      )}
                    </PerText>
                  }
                />
                <PerSpacer ml="xxs" />
                <PerText appearance="label-small" color="default" align="left" mb="none" mt="none">
                  {t('rankingView.infoBox.performance.performanceAssignment')}
                </PerText>
                <PerSpacer ml="xs" />
                <PerIcon
                  name={!!settings.tagAssignmentIsInProgress ? 'check_stroke' : 'close_stroke'}
                  color={!!settings.tagAssignmentIsInProgress ? 'success' : 'secondary'}
                  size={!!settings.tagAssignmentIsInProgress ? 18 : 14}
                />
                <PerSpacer ml="xxxs" />
                <PerText
                  appearance="label-small"
                  color={!!settings.tagAssignmentIsInProgress ? 'success' : 'secondary-lighter'}
                  fontWeight="normal"
                  align="left"
                  mb="none"
                  mt="none"
                >
                  {!!settings.tagAssignmentIsInProgress
                    ? t('rankingView.infoBox.performance.open')
                    : t('rankingView.infoBox.performance.closed')}
                </PerText>
              </PerFlexRow>
            </PerFlexRow>
          }
        >
          {availableEmployeeTags
            .filter((t) => isPerformanceTag(t.tagName as EmployeeTagType))
            .filter(
              (t) => t.tagName !== EmployeeTagType.Top && t.tagName !== EmployeeTagType.Low && t.tagName !== EmployeeTagType.EntryLevelLow,
            )
            .map((employeeTag, index) => {
              return (
                <React.Fragment key={index}>
                  <PerInfoBoxTagElement
                    name={t(`global.employeeTags.general.${employeeTag.tagName}`)}
                    color={tagColor(employeeTag.tagName)}
                    number={settings.rankingIsDisabled ? 0 : employeeTag.value}
                  />
                </React.Fragment>
              )
            })}
          <PerInfoBoxTagElement
            name={t('rankingView.infoBox.performance.untagged')}
            color={tagColor('untagged')}
            number={settings.rankingIsDisabled ? 0 : performanceUntaggedCount}
          />
          <PerInfoBoxTagElement
            name={t(`global.employeeTags.general.${EmployeeTagType.Low}`)}
            color={tagColor(EmployeeTagType.Low)}
            number={settings.rankingIsDisabled ? 0 : lowCounts}
            numberColor={minLowTagsNecessary <= lowCounts ? undefined : 'error'}
            outOf={minLowTagsNecessary + ' ' + t('rankingView.infoBox.performance.minLowTagsPossibleLabel')}
          />
          <PerInfoBoxTagElement
            name={t(`global.employeeTags.general.${EmployeeTagType.Top}`)}
            color={tagColor(EmployeeTagType.Top)}
            number={settings.rankingIsDisabled ? 0 : topCounts}
            numberColor={maxTopTagsNecessary >= topCounts ? undefined : 'error'}
            outOf={maxTopTagsNecessary + ' ' + t('rankingView.infoBox.performance.maxTopTagsPossibleLabel')}
          />
        </InfoBoxTile>

        <PerInfoBoxDivider />

        <InfoBoxTile heading={t('rankingView.infoBox.impactOfLoss.heading')} headingAlign="left">
          {availableEmployeeTags
            .filter((t) => isImpactOfLossTag(t.tagName as EmployeeTagType))
            .map((employeeTag, index) => {
              return (
                <React.Fragment key={index}>
                  {
                    <PerInfoBoxTagElement
                      name={t(`global.employeeTags.impactOfLoss.${employeeTag.tagName}`)}
                      color={tagColor(employeeTag.tagName)}
                      number={employeeTag.value}
                    />
                  }
                </React.Fragment>
              )
            })}
          <PerInfoBoxTagElement
            name={t('rankingView.infoBox.impactOfLoss.untagged')}
            color={tagColor('untagged')}
            number={impactOfLossUntaggedCount}
          />
        </InfoBoxTile>

        <PerInfoBoxDivider />

        <InfoBoxTile heading={t('rankingView.infoBox.riskOfLoss.heading')} headingAlign="left">
          {availableEmployeeTags
            .filter((t) => isRiskOfLossTag(t.tagName as EmployeeTagType))
            .map((employeeTag, index) => {
              return (
                <React.Fragment key={index}>
                  {
                    <PerInfoBoxTagElement
                      name={t(`global.employeeTags.riskOfLoss.${employeeTag.tagName}`)}
                      color={tagColor(employeeTag.tagName)}
                      number={employeeTag.value}
                    />
                  }
                </React.Fragment>
              )
            })}
          <PerInfoBoxTagElement
            name={t('rankingView.infoBox.impactOfLoss.untagged')}
            color={tagColor('untagged')}
            number={riskOfLossUntaggedCount}
          />
        </InfoBoxTile>

        {!isHR && (
          <>
            <PerInfoBoxDivider />
            {renderSettings()}
          </>
        )}
      </PerInfoBox>
    </div>
  )
}
